import { Divider, Grid } from '@mui/material';
import { CerebroComplexListItem, CerebroLabel, CerebroText } from '../../shared/components';
import { useAuth } from '../../shared/context';
import { DealDebtSchedule } from '../../shared/models';
import { BooleanUtils, DateUtils, EnumUtils, NumberUtils } from '../../shared/utils';

interface DebtScheduleListItemProps {
	schedule: DealDebtSchedule;
	onEdit: (schedule: DealDebtSchedule) => void;
	onDelete: (schedule: DealDebtSchedule) => void;
}

export const DebtScheduleListItem: React.FC<DebtScheduleListItemProps> = ({ schedule, onEdit, onDelete }) => {
	const { currentUser } = useAuth();
	const tags = schedule.isTermLoan ? ['Term Loan'] : ['Revolver / Line of Credit'];

	const lenderType = EnumUtils.GetLenderTypeDescription(schedule.lenderType);
	const facilityType = EnumUtils.GetFacilityTypeDescription(schedule.facilityType);

	const deleteTitle = `Delete the ${lenderType} ${facilityType}`;

	return (
		<CerebroComplexListItem
			tags={tags}
			sx={{ marginY: 2 }}
			onEdit={() => onEdit(schedule)}
			onDelete={() => onDelete(schedule)}
			onDeleteTitle={deleteTitle}
			showDelete={true}
		>
			<Grid container spacing={3} alignItems="flex-end">
				<Grid item xs={6} md={4}>
					<CerebroLabel>Original Loan Amount</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{NumberUtils.FormatCurrency(schedule.originalLoanAmount, '--')}
					</CerebroText>
				</Grid>
				<Grid item xs={6} md={4}>
					<CerebroLabel>Present Balance</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{NumberUtils.FormatCurrency(schedule.presentBalance, '--')}
					</CerebroText>
				</Grid>
				<Grid item xs={6} md={4}>
					<CerebroLabel>Collateral / Security</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{EnumUtils.GetCollateralSecurityTypeDescription(schedule.collateralSecurityType) ?? '--'}
					</CerebroText>
				</Grid>
				<Grid item xs={6} md={4}>
					<CerebroLabel>Interest Rate</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{schedule.interestRate ? schedule.interestRate.toFixed(3) + '%' : '--'}
					</CerebroText>
				</Grid>
				<Grid item xs={6} md={4}>
					<CerebroLabel>Refinancing?</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{BooleanUtils.YesNo(schedule.isRefinance) ?? '--'}
					</CerebroText>
				</Grid>

				{schedule.lenderType != null && (
					<Grid item xs={6} md={4}>
						<CerebroLabel>Lender Type</CerebroLabel>
						<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
							{EnumUtils.GetLenderTypeDescription(schedule.lenderType)}
						</CerebroText>
					</Grid>
				)}

				{schedule.amortization != null && (
					<Grid item xs={6} md={4}>
						<CerebroLabel>Amortization</CerebroLabel>
						<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
							{NumberUtils.FormatDecimal(schedule.amortization)}
						</CerebroText>
					</Grid>
				)}

				{schedule.monthlyPayment != null && (
					<Grid item xs={6} md={4}>
						<CerebroLabel>Monthly Payment</CerebroLabel>
						<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
							{NumberUtils.FormatCurrency(schedule.monthlyPayment)}
						</CerebroText>
					</Grid>
				)}

				{schedule.maturityDate != null && (
					<Grid item xs={6} md={4}>
						<CerebroLabel>Maturity Date</CerebroLabel>
						<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
							{DateUtils.DisplayDateOnly(schedule.maturityDate.toString())}
						</CerebroText>
					</Grid>
				)}

				{currentUser && currentUser.isEmployeeUser && schedule.notes && (
					<Grid item xs={12}>
						<CerebroLabel gutterBottom>
							Notes{' '}
							<CerebroText component="span" variant="hint">
								(Cerebro Only)
							</CerebroText>
						</CerebroLabel>
						<Divider />
						<CerebroText variant="small" sx={{ marginTop: 1.5, paddingX: { xs: 1, md: 2 } }}>
							{schedule.notes}
						</CerebroText>
					</Grid>
				)}
			</Grid>
		</CerebroComplexListItem>
	);
};
